// ═══════════════════════════════════════
// ACCOUNT-DELETE — Assistant de suppression de compte
//
// Un écran par décision, plutôt qu'un formulaire unique : le bouton rouge
// n'apparaît qu'au dernier écran, après le récapitulatif. Les fragments HTML
// viennent de api/modals.php et sont statiques (modal.js les met en cache) ;
// tout ce qui dépend du compte est injecté ici.
//
// L'état vit dans ce module et n'est envoyé qu'une fois, à la validation :
// un aller-retour par étape ne créerait que des suppressions à moitié faites.
// ═══════════════════════════════════════

import { authHeaders, clearSession } from './auth.js';
import { navigate, closeModal } from '../modal.js';
import { t } from '../i18n.js';

// ─────────────────────────────────────────
// État
// ─────────────────────────────────────────
let _preview = null;   // réponse de deletion-preview
let _steps   = [];     // clés de vues, dans l'ordre
let _current = 0;      // index dans _steps

const _choices = {
    partitions: null,  // { mode, heir_email?, fallback? }
    groups:     {},    // { <id>: { mode, heir_user_id? } }
    reason:     '',
};

function _reset() {
    _preview = null;
    _steps   = [];
    _current = 0;
    _choices.partitions = null;
    _choices.groups     = {};
    _choices.reason     = '';
}

// ─────────────────────────────────────────
// Entrée — appelée au montage de la vue « user-delete »
// ─────────────────────────────────────────
export async function startAccountDeletion(signal) {
    _reset();

    const summary = document.getElementById('dw-summary');
    if (summary) summary.innerHTML = `<li>${t('del.loading')}</li>`;

    try {
        const res = await fetch('/api/users.php?action=deletion-preview', { headers: authHeaders() });
        if (!res.ok) throw new Error('preview');
        _preview = await res.json();
    } catch (e) {
        _showError(t('del.error_load'));
        return;
    }

    _steps = _buildSteps();
    _renderIntro();
    _bindStep(signal);
}

/**
 * Les étapes sans objet sont sautées : un compte sans partition ni groupe ne
 * voit que l'introduction, le motif et la confirmation. Un écran par groupe.
 */
function _buildSteps() {
    const steps = ['user-delete'];

    if (_preview.partitions.total > 0) steps.push('user-delete-partitions');
    _preview.groups.forEach(() => steps.push('user-delete-group'));
    if (_preview.subscription.has_stripe) steps.push('user-delete-subscription');

    steps.push('user-delete-reason', 'user-delete-confirm');
    return steps;
}

// ─────────────────────────────────────────
// Montage — appelé à chaque vue de l'assistant
// ─────────────────────────────────────────
export function mountDeleteStep(signal) {
    const root = document.getElementById('delete-wizard');
    if (!root) return;

    // Vue « intro » : le montage passe par startAccountDeletion()
    if (root.dataset.step === 'intro') return;

    // Rechargement direct d'une étape sans aperçu (retour arrière brutal,
    // cache de modale) : on repart de l'introduction.
    if (!_preview) { navigate('user-delete'); return; }

    _renderStepCounter();

    switch (root.dataset.step) {
        case 'partitions':   _renderPartitions();   break;
        case 'group':        _renderGroup();        break;
        case 'subscription': _renderSubscription(); break;
        case 'reason':       _renderReason();       break;
        case 'confirm':      _renderRecap();        break;
    }

    _bindStep(signal);
}

// ─────────────────────────────────────────
// Navigation
// ─────────────────────────────────────────
function _bindStep(signal) {
    document.getElementById('dw-next')
        ?.addEventListener('click', () => _next(), { signal });
    document.getElementById('dw-back')
        ?.addEventListener('click', () => _back(), { signal });
    document.getElementById('dw-skip')
        ?.addEventListener('click', (e) => { e.preventDefault(); _next(true); }, { signal });
    document.getElementById('dw-submit')
        ?.addEventListener('click', () => _submit(), { signal });

    // Les blocs conditionnels suivent le choix affiché
    document.querySelectorAll('input[name="dw-partitions"]').forEach(el => {
        el.addEventListener('change', () => {
            const block = document.getElementById('dw-heir-block');
            if (block) block.hidden = el.value !== 'bequeath' || !el.checked;
        }, { signal });
    });
    document.querySelectorAll('input[name="dw-group"]').forEach(el => {
        el.addEventListener('change', () => {
            const block = document.getElementById('dw-group-heir-block');
            if (block) block.hidden = el.value !== 'bequeath' || !el.checked;
        }, { signal });
    });
}

function _next(skip = false) {
    if (!skip && !_collect()) return;
    if (skip) _choices.reason = '';

    if (_current >= _steps.length - 1) return;
    _current++;
    _goToCurrent();
}

function _back() {
    if (_current === 0) { navigate('user-edit'); return; }
    _current--;
    _goToCurrent();
}

/**
 * Deux écrans « groupe » consécutifs sont la même vue : navigate() la
 * rechargerait à l'identique, sans repasser par le rendu utile. Dans ce cas
 * seulement, on redessine à la main.
 */
function _goToCurrent() {
    const target  = _steps[_current];
    const current = document.getElementById('delete-wizard')?.dataset.step;

    if (target === 'user-delete-group' && current === 'group') {
        _renderStepCounter();
        _renderGroup();
        return;
    }
    navigate(target);
}

/** Le groupe traité se déduit de la position dans la liste des étapes. */
function _groupIndex() {
    const first = _steps.indexOf('user-delete-group');
    return first === -1 ? 0 : Math.max(0, _current - first);
}

// ─────────────────────────────────────────
// Collecte des réponses de l'écran courant
// ─────────────────────────────────────────
function _collect() {
    const step = document.getElementById('delete-wizard')?.dataset.step;

    if (step === 'partitions') {
        const mode = document.querySelector('input[name="dw-partitions"]:checked')?.value;
        if (!mode) return _showError(t('del.err.choice'));

        if (mode === 'bequeath') {
            const email = document.getElementById('dw-heir-email')?.value.trim() ?? '';
            if (!email) return _showError(t('del.err.email'));

            const fallback = document.querySelector('input[name="dw-fallback"]:checked')?.value;
            if (!fallback) return _showError(t('del.err.fallback'));

            _choices.partitions = { mode, heir_email: email, fallback };
        } else {
            _choices.partitions = { mode };
        }
    }

    if (step === 'group') {
        const group = _preview.groups[_groupIndex()];
        const mode  = document.querySelector('input[name="dw-group"]:checked')?.value;
        if (!mode) return _showError(t('del.err.choice'));

        if (mode === 'bequeath') {
            const heir = parseInt(document.getElementById('dw-group-heir')?.value ?? '0', 10);
            if (!heir) return _showError(t('del.err.heir'));
            _choices.groups[group.id] = { mode, heir_user_id: heir };
        } else {
            _choices.groups[group.id] = { mode };
        }
    }

    if (step === 'subscription' && !document.getElementById('dw-sub-ack')?.checked) {
        return _showError(t('del.err.ack'));
    }

    if (step === 'reason') {
        _choices.reason = document.getElementById('dw-reason')?.value.trim() ?? '';
    }

    _showError('');
    return true;
}

// ─────────────────────────────────────────
// Rendus
// ─────────────────────────────────────────
function _renderStepCounter() {
    const el = document.getElementById('dw-step');
    if (el) el.textContent = t('del.step', { n: _current + 1, total: _steps.length });
}

function _renderIntro() {
    _current = 0;
    _renderStepCounter();

    const p     = _preview.partitions;
    const items = [];

    if (p.total > 0) {
        const detail = [];
        if (p.public  > 0) detail.push(t('del.sum.public',  { n: p.public }));
        if (p.private > 0) detail.push(t('del.sum.private', { n: p.private }));
        items.push(t('del.sum.partitions', { n: p.total })
                   + (detail.length ? ` (${detail.join(', ')})` : ''));
    }

    const nbGroups = _preview.groups.length + _preview.empty_groups.length;
    if (nbGroups > 0) items.push(t('del.sum.groups', { n: nbGroups }));

    if (_preview.subscription.has_stripe) {
        let line = t('del.sum.subscription', { plan: _preview.subscription.plan });
        if (_preview.subscription.engagement_active) {
            line += ' — ' + t('del.sum.engagement', { date: _preview.subscription.engagement_ends_at });
        }
        items.push(line);
    }

    if (!items.length) items.push(t('del.sum.none'));

    const ul = document.getElementById('dw-summary');
    if (ul) ul.innerHTML = items.map(i => `<li>${_esc(i)}</li>`).join('');
}

function _renderPartitions() {
    const p    = _preview.partitions;
    const lead = document.getElementById('dw-part-lead');
    if (lead) {
        lead.textContent = t('del.part.lead', {
            n:    p.total,
            pub:  p.public,
            priv: p.private,
        });
    }

    const saved = _choices.partitions;
    if (saved) {
        const radio = document.querySelector(`input[name="dw-partitions"][value="${saved.mode}"]`);
        if (radio) radio.checked = true;

        const block = document.getElementById('dw-heir-block');
        if (block) block.hidden = saved.mode !== 'bequeath';

        if (saved.mode === 'bequeath') {
            const input = document.getElementById('dw-heir-email');
            if (input) input.value = saved.heir_email ?? '';
            const fb = document.querySelector(`input[name="dw-fallback"][value="${saved.fallback}"]`);
            if (fb) fb.checked = true;
        }
    }
}

function _renderGroup() {
    const _gIndex = _groupIndex();
    const group   = _preview.groups[_gIndex];
    if (!group) return;

    const lead = document.getElementById('dw-group-lead');
    if (lead) {
        lead.textContent = t('del.group.lead', {
            name:  group.name,
            i:     _gIndex + 1,
            total: _preview.groups.length,
            n:     group.nb_members + group.nb_emails,
        });
    }

    const select = document.getElementById('dw-group-heir');
    if (select) {
        if (!group.members.length) {
            // Personne à qui confier le groupe : seule la suppression reste.
            select.innerHTML = '';
            const bequeath = document.querySelector('input[name="dw-group"][value="bequeath"]');
            if (bequeath) {
                bequeath.disabled = true;
                bequeath.closest('.dw-option')?.classList.add('dw-option--disabled');
            }
        } else {
            select.innerHTML = '<option value="">' + _esc(t('del.group.choose')) + '</option>'
                + group.members.map(m =>
                    `<option value="${m.user_id}">${_esc(m.username)} — ${_esc(m.email)}`
                    + ` (${_esc(m.ensemble ? t('del.ensemble') : t('del.no_ensemble'))})</option>`
                  ).join('');
        }
    }

    const saved = _choices.groups[group.id];
    if (saved) {
        const radio = document.querySelector(`input[name="dw-group"][value="${saved.mode}"]`);
        if (radio) radio.checked = true;

        const block = document.getElementById('dw-group-heir-block');
        if (block) block.hidden = saved.mode !== 'bequeath';
        if (saved.heir_user_id && select) select.value = String(saved.heir_user_id);
    } else {
        document.querySelectorAll('input[name="dw-group"]').forEach(el => { el.checked = false; });
        const block = document.getElementById('dw-group-heir-block');
        if (block) block.hidden = true;
    }
}

function _renderSubscription() {
    const s    = _preview.subscription;
    const lead = document.getElementById('dw-sub-lead');
    if (!lead) return;

    let line = t('del.sub.lead', { plan: s.plan });
    if (s.engagement_active) line += ' ' + t('del.sub.engagement', { date: s.engagement_ends_at });
    lead.textContent = line;
}

function _renderReason() {
    const el = document.getElementById('dw-reason');
    if (el) {
        el.value = _choices.reason;
        setTimeout(() => el.focus(), 50);
    }
}

function _renderRecap() {
    const lines = [];
    const p     = _choices.partitions;

    if (p) {
        if (p.mode === 'bequeath') {
            lines.push(t('del.recap.partitions.bequeath', { email: p.heir_email }));
            lines.push(t('del.recap.fallback.' + p.fallback));
        } else {
            lines.push(t('del.recap.partitions.' + p.mode));
        }
    }

    _preview.groups.forEach(g => {
        const c = _choices.groups[g.id];
        if (!c) return;
        if (c.mode === 'bequeath') {
            const heir = g.members.find(m => m.user_id === c.heir_user_id);
            lines.push(t('del.recap.group.bequeath', { group: g.name, name: heir?.username ?? '' }));
        } else {
            lines.push(t('del.recap.group.delete', { group: g.name }));
        }
    });

    if (_preview.empty_groups.length) {
        lines.push(t('del.recap.groups_empty', { n: _preview.empty_groups.length }));
    }

    if (_preview.subscription.has_stripe) lines.push(t('del.recap.subscription'));

    lines.push(_choices.reason
        ? t('del.recap.reason',    { reason: _choices.reason })
        : t('del.recap.no_reason'));

    const ul = document.getElementById('dw-recap');
    if (ul) ul.innerHTML = lines.map(l => `<li>${_esc(l)}</li>`).join('');

    setTimeout(() => document.getElementById('delete-password')?.focus(), 50);
}

// ─────────────────────────────────────────
// Envoi
// ─────────────────────────────────────────
async function _submit() {
    const password = document.getElementById('delete-password')?.value ?? '';
    if (!password) { _showError(t('del.err.password')); return; }

    const btn = document.getElementById('dw-submit');
    if (btn) { btn.disabled = true; btn.textContent = t('del.submitting'); }

    try {
        const res = await fetch('/api/users.php?action=account', {
            method:  'DELETE',
            headers: authHeaders(),
            body:    JSON.stringify({
                password,
                reason:     _choices.reason,
                partitions: _choices.partitions ?? {},
                groups:     _choices.groups,
            }),
        });
        const data = await res.json();

        if (res.ok) {
            clearSession();
            closeModal();
            alert(t('auth.account_deleted'));
            location.href = '/';
            return;
        }
        _showError(data.error || t('del.error_generic'));
    } catch (e) {
        _showError(t('auth.error.network'));
    }

    if (btn) { btn.disabled = false; btn.textContent = t('del.btn_confirm'); }
}

// ─────────────────────────────────────────
// Utilitaires
// ─────────────────────────────────────────
function _showError(msg) {
    const el = document.getElementById('dw-error');
    if (el) el.textContent = msg;
    return false;
}

function _esc(str) {
    return String(str ?? '').replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
}
